// Web Push on the client: permission, subscription and "was the app opened from a notification?".
// iOS only allows push for a web app on the home screen (iOS 16.4+), and only after a tap.

import { isStandalone, detect } from './install.js';

export type PushSupport = 'ok' | 'needs-install' | 'unsupported' | 'insecure';

export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return 'insecure';
  const apis = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (detect().platform === 'ios' && !isStandalone()) return 'needs-install';
  return apis ? 'ok' : 'unsupported';
}

export function permission(): NotificationPermission | 'n/a' {
  return 'Notification' in window ? Notification.permission : 'n/a';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export interface SubscribeResult { ok: boolean; reason?: 'denied' | 'dismissed' | 'error'; service?: string; ms?: number }

/** Must be called from a click handler (iOS requires a user gesture for the permission prompt). */
export async function subscribe(session: string): Promise<SubscribeResult> {
  const t0 = performance.now();
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: perm === 'denied' ? 'denied' : 'dismissed' };
    const reg = await navigator.serviceWorker.ready;
    const { publicKey } = await (await fetch('/api/push/key')).json() as { publicKey: string };
    let sub = await reg.pushManager.getSubscription();
    // A subscription made with another server key (e.g. after new VAPID keys) must be replaced
    if (sub && sub.options.applicationServerKey) {
      const current = btoa(String.fromCharCode(...new Uint8Array(sub.options.applicationServerKey))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      if (current !== publicKey) { await sub.unsubscribe(); sub = null; }
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
    const res = await fetch('/api/push/subscribe', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session, subscription: sub.toJSON() }),
    });
    if (!res.ok) return { ok: false, reason: 'error' };
    const { service } = await res.json() as { service: string };
    return { ok: true, service, ms: Math.round(performance.now() - t0) };
  } catch (e) {
    console.warn('push subscribe failed', e);
    return { ok: false, reason: 'error' };
  }
}

export async function schedule(session: string, kind: 'story' | 'test', delaySeconds: number, name = ''): Promise<boolean> {
  try {
    const res = await fetch('/api/push/schedule', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session, kind, delay: delaySeconds, name }),
    });
    return res.ok;
  } catch { return false; }
}

export interface SendResult { ok: boolean; at: number; status?: number; reason?: string; service: string; subject: string }

/** Asks the server how the last send went (polls until a result newer than `since` is there). */
export async function deliveryResult(session: string, since: number, timeoutMs = 20000): Promise<SendResult | null> {
  const end = Date.now() + timeoutMs;
  while (Date.now() < end) {
    try {
      const { last } = await (await fetch(`/api/push/status?session=${encodeURIComponent(session)}`, { cache: 'no-store' })).json() as { last: SendResult | null };
      if (last && last.at >= since) return last;
    } catch { /* offline for a moment: try again */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
  return null;
}

/** Human-readable explanation of a failed send (Dutch, for the backstage panel). */
export function explainFailure(r: SendResult): string {
  const why: Record<string, string> = {
    BadJwtToken: 'het ondertekende token werd geweigerd: controleer VAPID_SUBJECT (https: of mailto:) en de klok van de server',
    ExpiredJwtToken: 'het token is verlopen: de klok van de server loopt waarschijnlijk niet juist',
    VapidPkHashMismatch: 'de VAPID-sleutel van de server verschilt van die waarmee de gsm zich abonneerde: abonneer opnieuw',
    BadVapidPublicKey: 'de publieke VAPID-sleutel is ongeldig',
    BadAuthorizationHeader: 'de Authorization-header is ongeldig',
  };
  return `${r.status ?? 'netwerkfout'} ${r.reason ?? ''}${r.reason && why[r.reason] ? `: ${why[r.reason]}` : ''}`;
}

export interface PushOpen { kind: 'story' | 'test'; sentAt?: number; openedAt: number; coldStart: boolean }

/**
 * Resolves when the app is brought back by a tap on a notification.
 * Warm start: the service worker focuses this page and posts a message.
 * Cold start: the page is loaded with ?resume=… (see main.ts).
 */
export function onPushOpen(fn: (e: PushOpen) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => {};
  const handler = (ev: MessageEvent) => {
    const d = ev.data as { type?: string; kind?: 'story' | 'test'; sentAt?: number } | null;
    if (d?.type === 'push-open') fn({ kind: d.kind ?? 'story', sentAt: d.sentAt, openedAt: Date.now(), coldStart: false });
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}

export function clearBadge() {
  const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> };
  void nav.clearAppBadge?.().catch(() => {});
}
